/**
 * deep-signal-series-api.ts
 *
 * Builds the Performance page's funnel metrics (Mention / Top-5 / Primary
 * Recommendation) from the **Deep Signal** scoring runs rather than from Brand
 * Monitoring. Each Deep Signal run is one measurement in time, so the runs of a
 * customer form a real longitudinal series: baseline + every re-measurement,
 * plotted left-to-right by run timestamp, per model.
 *
 * The rates Deep Signal stores are 0–1 (mention_rate, top5_rate,
 * primary_rec_rate); the Performance charts and KPI cards work in percent, so
 * everything is scaled to 0–100 here.
 *
 * Lane scoping (both rules applied):
 *  - Every lane a run MEASURED plots as its own line, including a measurement-only
 *    lane like "ChatGPT (Web)" (gptweb) and a lane that measured a real 0.0 (a
 *    measured zero is a result, not an absence — it plots as a flat zero line).
 *  - The blended `overall` is the mean over only this client's blend-eligible
 *    lanes (`blend_eligible_models`, decided by the server per client): gptweb and
 *    any lane added after the client's baseline (e.g. AI Mode, Gemini for Iterate)
 *    render as their own line but stay OUT of the blend so they never double-count
 *    the base provider or distort the headline.
 *  - A lane a run did NOT measure is left unset → a gap on that line, never
 *    backfilled to the blended overall (so a lane's line begins at its first real
 *    measurement instead of being drawn back to the series start).
 *
 * WHICH RUNS PLOT: whatever the server marks `series_eligible`. That one flag is
 * the whole decision — an admin exclusion, an admin deliberate force-include, a
 * pre-flight/calibration label, and the scored-row floor (a run must carry at
 * least SERIES_SCORED_FLOOR of the customer's largest run to count as a
 * comparable measurement). It is computed in ONE place on the server
 * (app/services/performance_series/eligibility.py) and shared with the
 * /analytics/series envelope path, so the two can never disagree.
 *
 * This file used to filter on `excluded_from_series` alone. That boolean can only
 * say "an admin hid this run"; it says nothing about size. The floor existed, but
 * only on the envelope path — which sits behind an off-by-default UI flag, so it
 * never ran for the chart clients actually see. A pre-flight probe on customer 8
 * (run 36: one question, six lanes, one iteration — 6 scored rows against a
 * 1,500-row baseline) therefore plotted as a full measurement point and spiked
 * the client chart until a human noticed and hid it by hand.
 */
import {
  listDeepSignalRuns,
  getDeepSignalData,
  type DeepSignalData,
  type DSModelBreakdown,
  type DSRun,
} from "@/api/deep-signal-api";
import { resolveBrandCustomer } from "@/app/services/brand/resolve-customer";
import { getPublicationOutcomes } from "@/api/content";
import type { PerformanceEvent } from "@/app/data/performance-events";
import { isExcludedFromAnalytics } from "@/app/constants/models";
import { isIncomplete, type RunCompleteness } from "@/api/run-completeness";
import type { RunSeries, SeriesRebaseline } from "@/api/series-rebaseline";

/** A plotted run whose excluded + failed calls exceed the threshold. */
export interface SeriesIncompleteRun {
  date: string;
  timestamp: number;
  completeness: RunCompleteness;
}

/** Where a series rebaseline falls on the chart: between the last plotted read
 *  before it and the first plotted read after it. The line is broken there. */
export interface SeriesRebaselineMark {
  /** x position of the marker (the apply date when it falls between the two
   *  reads, else their midpoint). */
  timestamp: number;
  /** The last plotted read before, and the first after (the new baseline). */
  beforeTimestamp: number;
  afterTimestamp: number;
  afterDate: string;
  /** Every rebaseline applied between those two reads (normally one). */
  rebaselines: SeriesRebaseline[];
  /** The question battery changed between the two reads with no rebaseline
   *  covering it (#137): the line breaks and the reads are not comparable. */
  batteryChanged?: boolean;
}

/** Key on each point carrying its series segment. Not a model key, so no chart
 *  draws it as a line; the chart splits lines where it changes. */
export const SEGMENT_KEY = "__segment";

export interface DSSeriesPoint {
  date: string;
  timestamp: number;
  overall: number;
  [modelKey: string]: number | string;
}

export interface DeepSignalSeriesBundle {
  selectedModels: string[];
  mention: DSSeriesPoint[];
  top5: DSSeriesPoint[];
  primaryRec: DSSeriesPoint[];
  /** Blended latest-run funnel (percent) for the KPI cards. null = no runs. */
  latest: { mention: number; top5: number; primaryRec: number } | null;
  runCount: number;
  /** Plotted runs flagged incomplete, oldest first. Empty when every run is
   *  complete, which is what keeps a clean series rendering as before. */
  incompleteRuns: SeriesIncompleteRun[];
  /** The latest plotted run's verdict, for the KPI cards. */
  latestCompleteness: RunCompleteness | null;
  /** Series rebaselines between plotted reads, oldest first. Empty for a
   *  series never rebaselined, which therefore draws one unbroken line. */
  rebaselineMarks: SeriesRebaselineMark[];
  /** The latest plotted run's series segment, for the KPI cards' label. */
  latestSeries: RunSeries | null;
  /** The rebaseline that opened the latest plotted run's segment, if any. */
  latestRebaseline: SeriesRebaseline | null;
  /** Publication records (#134) as timeline pins, each with its outcome.
   *  Empty when there are none or the outcomes endpoint is unavailable. */
  publicationEvents: PerformanceEvent[];
}

type MetricField = keyof Pick<DSModelBreakdown, "mention_rate" | "top5_rate" | "primary_rec_rate">;

const EMPTY: DeepSignalSeriesBundle = {
  selectedModels: [], mention: [], top5: [], primaryRec: [], latest: null, runCount: 0,
  incompleteRuns: [], latestCompleteness: null,
  rebaselineMarks: [], latestSeries: null, latestRebaseline: null, publicationEvents: [],
};

/** Publication records as chart pins: date, title and what it moved. */
async function publicationEventsFor(customerId: number): Promise<PerformanceEvent[]> {
  try {
    const view = await getPublicationOutcomes(customerId);
    return (view.marks ?? []).map(m => ({
      id: `publication-${m.publication_id}`,
      date: m.published_at,
      title: `Published: ${m.title || "Untitled"} · ${m.status_label}${m.resolved_at_label ? ` at ${m.resolved_at_label}` : ""}`
        + (m.lasting_label ? ` · ${m.lasting_label}` : ""),
      description: m.note,
      type: "content" as const,
    }));
  } catch {
    return [];
  }
}

/** Rebaseline marks from the plotted points' segments. Segment k is opened by
 *  rebaselines[k - 1]; a change from segment a to b between two consecutive
 *  points crosses rebaselines a..b-1. */
export function rebaselineMarksFor(
  points: { timestamp: number; date: string; segment: number; batteryChanged?: boolean }[],
  rebaselines: SeriesRebaseline[],
): SeriesRebaselineMark[] {
  const marks: SeriesRebaselineMark[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (next.segment === prev.segment) {
      // A battery change no rebaseline covers (#137): a break with no event.
      if (next.batteryChanged) {
        marks.push({
          timestamp: (prev.timestamp + next.timestamp) / 2,
          beforeTimestamp: prev.timestamp,
          afterTimestamp: next.timestamp,
          afterDate: next.date,
          rebaselines: [],
          batteryChanged: true,
        });
      }
      continue;
    }
    const crossed = rebaselines.slice(Math.min(prev.segment, next.segment), Math.max(prev.segment, next.segment));
    if (!crossed.length) continue;
    const applied = Date.parse(crossed[crossed.length - 1].applied_at ?? "");
    const between = !Number.isNaN(applied) && applied > prev.timestamp && applied < next.timestamp;
    marks.push({
      timestamp: between ? applied : (prev.timestamp + next.timestamp) / 2,
      beforeTimestamp: prev.timestamp,
      afterTimestamp: next.timestamp,
      afterDate: next.date,
      rebaselines: crossed,
    });
  }
  return marks;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Best available timestamp for a run, in epoch ms. Sourced from the run-list row
// (which carries created_at, always set) rather than the per-run payload (whose
// run object only exposes started_at/completed_at and can be null for baseline /
// migrated runs). Without created_at a baseline run dropped to the index
// fallback → epoch ~1970 → it fell outside the chart window and disappeared,
// leaving a flat single-point "Baseline" line even though a later run existed.
function runTimestamp(run: DSRun, idx: number): number {
  const raw = run.completed_at ?? run.started_at ?? run.created_at ?? null;
  const t = raw ? Date.parse(String(raw)) : NaN;
  return Number.isNaN(t) ? idx : t;
}

// Does this run plot? `series_eligible` is the server's single, shared verdict
// and is authoritative whenever it is present.
//
// The fallback is for one case only: a frontend bundle talking to a backend that
// predates the field. It is deliberately the STRICTER of the two readings — the
// legacy boolean plus a client-side scored-row floor — because the failure that
// made this function exist was a tiny run plotting as a full measurement, and
// "the backend is old" must not quietly restore that. The floor mirrors
// SERIES_SCORED_FLOOR server-side; a run with no scored_count at all is kept,
// since an absent count is not evidence of a small run.
const SCORED_FLOOR_FRACTION = 0.5;
// Mirrors performance_series.eligibility.NON_SERIES_RUN_LABELS.
const NON_SERIES_RUN_LABELS: ReadonlySet<string> = new Set(["smoke_calibration", "verification"]);

// Exported because the Deep Signal page applies the same verdict to its run
// picker (#120): a run the chart does not plot is not a run the client sees.
export function isSeriesPoint(run: DSRun, _i?: number, all?: readonly DSRun[]): boolean {
  if (typeof run.series_eligible === "boolean") return run.series_eligible;
  if (run.excluded_from_series) return false;
  if (NON_SERIES_RUN_LABELS.has(run.run_label ?? "")) return false;

  const counts = (all ?? [])
    .filter(r => !r.excluded_from_series && !NON_SERIES_RUN_LABELS.has(r.run_label ?? ""))
    .map(r => r.scored_count)
    .filter((n): n is number => typeof n === "number");
  if (typeof run.scored_count !== "number" || !counts.length) return true;
  const floor = Math.max(1, Math.floor(Math.max(...counts) * SCORED_FLOOR_FRACTION));
  return run.scored_count >= floor;
}

async function buildBundle(brandName: string): Promise<DeepSignalSeriesBundle> {
  if (!brandName) return EMPTY;

  // One resolver for the whole app (see services/brand/resolve-customer): the
  // local bidirectional `includes()` this used to do matched any customer whose
  // name contained the brand — or that the brand's name contained — so an
  // unrelated customer could win outright.
  const match = await resolveBrandCustomer(brandName);
  if (!match) return EMPTY;

  const [{ runs, rebaselines = [] }, publicationEvents] = await Promise.all([
    listDeepSignalRuns(match.id), publicationEventsFor(match.id),
  ]);
  // Keep only the runs the server marks as series points, BEFORE anything else,
  // so a non-comparable run can never reach the chart, its blend, or the KPI
  // cards. Oldest run first → left-to-right timeline.
  const sorted = runs
    .filter(isSeriesPoint)
    .sort((a, b) => (a.run_sequence_number ?? 0) - (b.run_sequence_number ?? 0));
  if (!sorted.length) return EMPTY;

  // Pair each run-list row (carries created_at) with its loaded payload, keeping
  // only runs whose payload resolved. The run row drives the timestamp; the
  // payload drives the metric values.
  const loaded = await Promise.all(
    sorted.map(r => getDeepSignalData(match.id, r.id).catch(() => null)),
  );
  const pairs = sorted
    .map((run, i) => ({ run, data: loaded[i] }))
    .filter((p): p is { run: DSRun; data: DeepSignalData } => p.data != null);
  if (!pairs.length) return EMPTY;

  // Model union across every run. EVERY measured lane gets a line — including
  // measurement-only lanes (gptweb) and lanes that measured 0.0 — so the chart
  // shows what was actually measured. Blend eligibility (which lanes count toward
  // the headline) is a separate, per-run decision handled in pointFor; it does
  // not remove a lane's line.
  const modelSet = new Set<string>();
  for (const { data } of pairs) {
    for (const m of Object.keys(data.model_breakdown)) modelSet.add(m);
  }
  const selectedModels = [...modelSet];

  const mention: DSSeriesPoint[] = [];
  const top5: DSSeriesPoint[] = [];
  const primaryRec: DSSeriesPoint[] = [];
  const incompleteRuns: SeriesIncompleteRun[] = [];
  const segmentPoints: { timestamp: number; date: string; segment: number; batteryChanged?: boolean }[] = [];

  pairs.forEach(({ run, data }, idx) => {
    const ts = runTimestamp(run, idx);
    const date = typeof ts === "number" && ts > 1
      ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : `Run ${idx + 1}`;
    const completeness = run.completeness ?? data.run.completeness ?? null;
    if (isIncomplete(completeness)) incompleteRuns.push({ date, timestamp: ts, completeness });
    // Series segment: the chart breaks every line where this changes, so no
    // line is drawn across a rebaseline as if nothing changed.
    const series = run.series ?? data.run.series;
    const segment = series?.segment ?? 0;
    // The line breaks at a rebaseline and at a battery change no rebaseline
    // covers (#137); older payloads carry no line_segment.
    const lineSegment = series?.line_segment ?? segment;
    segmentPoints.push({ timestamp: ts, date, segment, batteryChanged: !!series?.battery_changed });

    // Lanes that count toward THIS run's blended overall. The server decides it
    // per client (a lane added after baseline renders as its own line but stays
    // out of the blend). Fall back to the global catalog rule only when an older
    // or cached payload omits the field — never treat an absent field as
    // "nothing eligible", which would zero the headline.
    const eligible = new Set(
      data.blend_eligible_models ?? selectedModels.filter(m => !isExcludedFromAnalytics(m)),
    );

    const pointFor = (field: MetricField): DSSeriesPoint => {
      const point: DSSeriesPoint = { date, timestamp: ts, overall: 0, [SEGMENT_KEY]: lineSegment };
      let sum = 0, n = 0;
      for (const m of selectedModels) {
        const stats = data.model_breakdown[m];
        // A lane this run measured plots its real value (a measured 0.0 included
        // — it is a result, not an absence). A lane this run did not measure is
        // left unset → a gap on that line, never backfilled. Only blend-eligible
        // lanes contribute to the headline mean.
        if (stats) {
          const v = (stats[field] ?? 0) * 100;
          point[m] = round2(v);
          if (eligible.has(m)) { sum += v; n += 1; }
        }
      }
      point.overall = n ? round2(sum / n) : 0;
      return point;
    };

    mention.push(pointFor("mention_rate"));
    top5.push(pointFor("top5_rate"));
    primaryRec.push(pointFor("primary_rec_rate"));
  });

  const last = mention.length - 1;
  const latest = last >= 0
    ? {
        mention: mention[last].overall,
        top5: top5[last].overall,
        primaryRec: primaryRec[last].overall,
      }
    : null;

  const lastPair = pairs[pairs.length - 1];
  const latestCompleteness = lastPair.run.completeness ?? lastPair.data.run.completeness ?? null;
  const latestSeries = lastPair.run.series ?? lastPair.data.run.series ?? null;
  const latestRebaseline = latestSeries && latestSeries.segment > 0
    ? rebaselines[latestSeries.segment - 1] ?? null
    : null;

  return {
    selectedModels, mention, top5, primaryRec, latest, runCount: mention.length,
    incompleteRuns, latestCompleteness,
    rebaselineMarks: rebaselineMarksFor(segmentPoints, rebaselines),
    latestSeries, latestRebaseline, publicationEvents,
  };
}

// ── Short-lived cache ─────────────────────────────────────────────────────────
// The Performance page builds three Deep-Signal-backed charts plus the KPI
// cards, all of which need this same bundle. Resolving a brand to its customer,
// listing runs, and fetching every run's full payload is several round-trips, so
// share one in-flight promise per brand for a short window. Cleared on
// "analytics:refresh" (Run Now) so fresh runs still load.
const _TTL_MS = 60_000;
const _cache = new Map<string, { at: number; p: Promise<DeepSignalSeriesBundle> }>();

if (typeof window !== "undefined") {
  window.addEventListener("analytics:refresh", () => _cache.clear());
}

export function fetchDeepSignalSeries(brandName: string): Promise<DeepSignalSeriesBundle> {
  const key = (brandName || "").toLowerCase();
  const now = Date.now();
  const hit = _cache.get(key);
  if (hit && now - hit.at < _TTL_MS) return hit.p;

  const p = buildBundle(brandName).catch(err => { _cache.delete(key); throw err; });
  _cache.set(key, { at: now, p });
  return p;
}
